import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash, FilePdf, Sparkle, Warning, CheckCircle, Receipt } from "@phosphor-icons/react";

export function ReceiptsDialog({
  receiptItem, onClose, receipts, uploadInvoice, uploadingInvoice,
  invoiceResult, applyInvoice, applying, deleteReceipt, BACKEND, money,
}) {
  const invoices = receipts.filter((r) => r.is_invoice);
  const invoiceSum = invoices.reduce((s, r) => s + (r.invoice_total || 0), 0);
  const taxSum = invoices.reduce((s, r) => s + (r.tax_amount || 0), 0);

  return (
    <Dialog open={!!receiptItem} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Receipt size={20} weight="bold" /> Invoices & Receipts
          </DialogTitle>
        </DialogHeader>
        {receiptItem && (
          <div className="space-y-4" data-testid="receipts-dialog">
            <div className="border border-border p-3 bg-secondary text-sm">
              <span className="font-medium">{receiptItem.category}</span> — {receiptItem.description}
            </div>

            {/* Single AI-powered upload */}
            <div className="border border-border p-4 space-y-2">
              <Label className="label-caps flex items-center gap-1">
                <Sparkle size={14} weight="fill" /> Upload invoice — AI reads total, sales tax & vendor
              </Label>
              <Input type="file" accept=".png,.jpg,.jpeg,.webp,.heic,.gif,.pdf" disabled={uploadingInvoice}
                onChange={(e) => uploadInvoice(e.target.files)} data-testid="invoice-file-input" className="mt-1 rounded-none" />
              {uploadingInvoice && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Sparkle size={12} weight="fill" className="animate-pulse" /> Reading invoice with AI…
                </p>
              )}
            </div>

            {/* Extraction result */}
            {invoiceResult && (
              <div className="border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="invoice-result">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <Field label="Subtotal" value={money(invoiceResult.subtotal_amount)} />
                  <Field label="Sales Tax" value={money(invoiceResult.tax_amount)} />
                  <Field label="Total" value={money(invoiceResult.total_amount)} strong />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {invoiceResult.vendor_name && (
                    <span className="flex items-center gap-1">
                      Vendor: <span className="font-medium text-foreground">{invoiceResult.vendor_name}</span>
                      {invoiceResult.vendor_matched && (
                        <span className="text-emerald-700 flex items-center gap-0.5" data-testid="vendor-matched-badge">
                          <CheckCircle size={12} weight="fill" /> linked
                        </span>
                      )}
                    </span>
                  )}
                  {invoiceResult.invoice_date && <span>Date: {invoiceResult.invoice_date}</span>}
                </div>
                {invoiceResult.summary && <p className="text-xs text-muted-foreground">{invoiceResult.summary}</p>}
                {invoiceResult.over_budget && (
                  <div className="flex items-center gap-2 border border-destructive text-destructive px-3 py-2 text-xs" data-testid="invoice-overspend-alert">
                    <Warning size={14} weight="fill" /> Over budget by {money(invoiceResult.over_by)} — this invoice exceeds the budgeted amount.
                  </div>
                )}
                <Button onClick={applyInvoice} disabled={applying} className="rounded-none w-full" data-testid="apply-invoice-button">
                  {applying ? "Applying…" : `Apply — set actual to ${money(invoiceSum || invoiceResult.total_amount)}`}
                </Button>
              </div>
            )}

            {/* Attached invoices / receipts */}
            {receipts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No invoices yet. Upload an invoice above and AI will extract the amounts.</p>
            ) : (
              <>
                {invoices.length > 0 && (
                  <div className="flex items-center justify-between border border-border px-3 py-2 text-sm bg-secondary" data-testid="invoice-rollup">
                    <span className="label-caps">{invoices.length} invoice{invoices.length === 1 ? "" : "s"} · tax {money(taxSum)}</span>
                    <span className="font-mono-fig font-semibold">{money(invoiceSum)}</span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  {receipts.map((r) => {
                    const url = `${BACKEND}/api/receipts/${r.id}/download`;
                    const isPdf = (r.content_type || "").includes("pdf");
                    return (
                      <div key={r.id} className="border border-border group relative" data-testid={`receipt-${r.id}`}>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="block h-24 flex items-center justify-center bg-secondary overflow-hidden">
                          {isPdf ? <FilePdf size={32} className="text-destructive" /> : <img src={url} alt={r.original_filename} className="w-full h-full object-cover" />}
                        </a>
                        {r.is_invoice && (
                          <div className="px-2 py-1 text-[11px] font-mono-fig border-t border-border flex items-center gap-1 text-primary">
                            <Sparkle size={10} weight="fill" /> {money(r.invoice_total)}
                          </div>
                        )}
                        <div className="px-2 py-1 text-[11px] truncate border-t border-border" title={r.original_filename}>{r.original_filename}</div>
                        <button onClick={() => deleteReceipt(r.id)} data-testid={`delete-receipt-${r.id}`}
                          className="absolute top-1 right-1 bg-background border border-border p-1 opacity-0 group-hover:opacity-100 transition-opacity text-destructive">
                          <Trash size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, strong }) {
  return (
    <div className="border border-border p-2">
      <div className="label-caps mb-1">{label}</div>
      <div className={`font-mono-fig tabular-nums ${strong ? "text-lg font-bold" : "text-sm"}`}>{value}</div>
    </div>
  );
}
